import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiData, ApiMessage, ApiResponse } from '../../interfaces/api/api-response.interface';
import { ToastrNotificationService } from '../ui/toastr-notification.service';
import { TRANSFER_RECEIPT_KEY } from '../../helpers/global/financial.constant';
import {
  PhysicalWithdrawCode,
  PhysicalWithdrawCodeHistory,
  TransferData,
} from '../../interfaces/api/financial.interface';
import {
  AgentNetworkNode,
  AgentNetworkSummaryFilter,
  RequestTransferData,
  TransactionAdmin,
  TransactionPlayer,
} from '../../interfaces/api/transaction.interface';
import { EncryptionService } from '../ui/encryption.service';

@Injectable({
  providedIn: 'root',
})
export class TransferService {
  public API_URL = `${environment.apiUrl}/transaction`;
  private apiReportsUrl = `${environment.apiReportsUrl}/transaction`;
  constructor(
    private _httpClient: HttpClient,
    private _notificationService: ToastrNotificationService,
    private _encryptionService: EncryptionService
  ) {}

  getAllTransactions(filter: object): Observable<ApiResponse<ApiData<TransactionAdmin[]>>> {
    const endpoint = `${this.apiReportsUrl}/find-all-load-and-withdraw-reports`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<TransactionAdmin[]>>>(endpoint, {
      params,
    });
  }

  getAllWithdrawalRequest(filter: object): Observable<ApiResponse<ApiData<TransactionAdmin[]>>> {
    const endpoint = `${this.API_URL}/find-all-withdrawal-request`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<TransactionAdmin[]>>>(endpoint, {
      params,
    });
  }

  getAllDepositRequest(filter: object): Observable<ApiResponse<ApiData<TransactionAdmin[]>>> {
    const endpoint = `${this.API_URL}/find-all-deposit-request`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<TransactionAdmin[]>>>(endpoint, {
      params,
    });
  }

  findReceiptNumberRepeated(filter: object): Observable<ApiResponse<ApiData<TransactionAdmin[]>>> {
    const endpoint = `${this.API_URL}/find-receipt-number-repeated`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<TransactionAdmin[]>>>(endpoint, {
      params,
    });
  }

  findAllTransactionsReports(filter: object): Observable<ApiResponse<ApiData<TransactionAdmin[]>>> {
    const endpoint = `${this.apiReportsUrl}/find-all-transaction-reports`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<TransactionAdmin[]>>>(endpoint, {
      params,
    });
  }

  findAllPhysicalTransactionPointsReports(
    filter: object
  ): Observable<ApiResponse<ApiData<TransactionAdmin[]>>> {
    const endpoint = `${this.apiReportsUrl}/find-all-physical-transaction-points-reports`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<TransactionAdmin[]>>>(endpoint, { params });
  }

  findAgentNetworkSummary(
    filter: AgentNetworkSummaryFilter
  ): Observable<ApiResponse<ApiData<AgentNetworkNode[]>>> {
    const endpoint = `${this.apiReportsUrl}/agent-network-summary`;
    let params = new HttpParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this._httpClient.get<ApiResponse<ApiData<AgentNetworkNode[]>>>(endpoint, {
      params,
      headers: { 'X-Skip-Error-Notification': 'true' },
    });
  }

  getAllByPlayer(filter: object): Observable<ApiResponse<ApiData<TransactionPlayer[]>>> {
    const data = this._encryptionService.encrypt(
      JSON.stringify({
        ...filter,
      })
    );
    const params = new HttpParams().set('data', data.toString());

    const endpoint = `${this.apiReportsUrl}/get-all-by-player`;
    return this._httpClient.get<ApiResponse<ApiData<TransactionPlayer[]>>>(endpoint, {
      params,
    });
  }

  public findPhysicalWithdrawByCode(code: string): Observable<ApiResponse<PhysicalWithdrawCode>> {
    const endpoint = `${environment.apiUrl}/physical-withdraw/store/find-by-code`;
    const params = new HttpParams({ fromObject: { code } });
    return this._httpClient.get<ApiResponse<PhysicalWithdrawCode>>(endpoint, { params });
  }

  public findAllPhysicalWithdrawCodesHistory(
    filter: object
  ): Observable<ApiResponse<ApiData<PhysicalWithdrawCodeHistory[]>>> {
    const endpoint = `${environment.apiUrl}/physical-withdraw/backoffice/history`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<PhysicalWithdrawCodeHistory[]>>>(endpoint, {
      params,
    });
  }

  public cancelPhysicalWithdrawCode(
    codeId: string,
    reason: string
  ): Observable<ApiResponse<PhysicalWithdrawCodeHistory>> {
    const endpoint = `${environment.apiUrl}/physical-withdraw/backoffice/${codeId}/cancel`;
    return this._httpClient.post<ApiResponse<PhysicalWithdrawCodeHistory>>(endpoint, { reason });
  }

  public saveTransfer(formData: FormData): Observable<ApiResponse<TransferData>> {
    return this._httpClient
      .post<ApiResponse<TransferData>>(`${this.API_URL}/save-transaction`, formData)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public saveTransferPlayers(formData: FormData): Observable<ApiResponse<TransferData>> {
    const dataObject: Record<string, any> = {};
    formData.forEach((value, key) => {
      if (key !== TRANSFER_RECEIPT_KEY) dataObject[key] = value;
    });

    const receiptFile = formData.get(TRANSFER_RECEIPT_KEY);
    const encryptedData = this._encryptionService.encrypt(JSON.stringify(dataObject));
    const newFormData = new FormData();
    newFormData.append('data', encryptedData);
    if (receiptFile instanceof File) newFormData.append(TRANSFER_RECEIPT_KEY, receiptFile);

    return this._httpClient
      .post<
        ApiResponse<TransferData>
      >(`${this.API_URL}/save-transaction-players`, newFormData, { headers: { 'X-Skip-Error-Notification': 'true' } })
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public setRequestTransfer(
    requestData: RequestTransferData
  ): Observable<ApiResponse<TransferData>> {
    return this._httpClient
      .post<ApiResponse<TransferData>>(`${this.API_URL}/set-request-transaction`, requestData)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  private showNotification(message: ApiMessage): void {
    this._notificationService.showNotification({
      title: 'transfer.title',
      message: message,
      type: 'success',
    });
  }
}
